import React, { useState, useMemo } from 'react';
import notify from './utils/notify';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiChevronRight, FiBarChart2, FiGrid, FiEdit as FiEditIcon } from 'react-icons/fi';
import './ScorecardControlCentre.css';
import CourseStatsBar from './components/CourseStatsBar';
import CourseStatsEditModal from './components/CourseStatsEditModal';
import HandicapEditChoiceModal from './components/HandicapEditChoiceModal';
import HandicapKeypadModal from './components/HandicapKeypadModal';
import ScoreMarker from './components/ScoreMarker';

// The Rounds page's Team-Only counterpart to the Players/Scoring/Finalise tabs
// in NewScorecardEntry.js. Kept as a fully separate component (not branches
// threaded into that 2250-line file) for the same reason TeamLiveScoring.js
// was kept separate from LiveScoring.js: a Team-Only round (Foursomes/
// Greensomes/Texas Scramble) has no individual player/Round data at all, so
// there is nothing to share with the per-player rendering logic — isolating
// it here means the individual-scoring path can't be regressed by this code.
//
// teamCompetition: { id, template, name, teams: [{id, name, member_names,
//   preview: {team_handicap, formula, members:[{player_id,name,
//   handicap_index,course_handicap,weight_pct}]} | null}] } | null
// teamRounds: TeamRound.to_dict() rows (already includes hole_scores,
//   member_handicaps_snapshot with the same per-member shape as `preview`)
// canStart/canFinalize are passed separately (not one combined "canManage")
// because they gate different backend permissions — MANAGE_COMPETITIONS for
// starting team rounds vs. FINALIZE_ROUND for finalize/reopen — so a button
// is only shown to someone who'd actually be allowed to call the endpoint.
// canEditCourseStats/canEditAllowance mirror NewScorecardEntry.js's own two
// permission booleans exactly (MANAGE_COURSES vs EDIT_TOURNAMENT/creator) —
// passed down rather than recomputed here so there's one source of truth.
// canEditHandicapIndex mirrors that page's own MANAGE_PLAYERS-or-owner check
// — same permission gates the "Handicap Index" choice for both pages.
// Mixed tees, shared ball. Two separate decisions live on this panel:
//   * which tee each PLAYER drives from — supplies their own Course Rating and
//     Slope, exactly as in an individual round; and
//   * which tee is NOMINATED for scoring — supplies the par used in every
//     member's Course Handicap and the card the side scores against.
// The WHS GB&I guidance requires the second whenever one ball is shared across
// tees (Section IV p.48), and nominating is precisely what removes the need for
// any further adjustment in any format.
const TeamRoundPanel = ({
  tournamentId, roundNumber, teamCompetition, teamRounds, isCurrentRoundFinalized,
  viewMode, setViewMode, canStart, canFinalize, course, allowancePercent,
  canEditCourseStats, canEditAllowance, canEditHandicapIndex, queryClient, authenticatedFetch,
  courseTees,
}) => {
  const navigate = useNavigate();
  const [isStarting, setIsStarting] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isCourseStatsModalOpen, setIsCourseStatsModalOpen] = useState(false);
  const [savingScoringTee, setSavingScoringTee] = useState(false);
  // Which tee the course-stats modal is editing; null means the allowance row.
  const [editingTee, setEditingTee] = useState(null);
  const [courseStatsShowHoles, setCourseStatsShowHoles] = useState(false);

  // Click-to-edit member handicaps — mirrors NewScorecardEntry.js's own
  // openHcpEdit/handleHcpKeypadSave/handleSavePlayerIndex state exactly, just
  // pointed at the team endpoints. customHandicaps holds PENDING pre-start
  // overrides (nothing persists until Start Team Rounds is clicked);
  // livePreviews holds the server-recomputed preview after such an edit, so
  // the shown team handicap updates immediately without ever re-deriving the
  // weighting math on the client.
  const [hcpChoiceMember, setHcpChoiceMember] = useState(null);
  const [hcpKeypadMember, setHcpKeypadMember] = useState(null);
  const [hcpEditMode, setHcpEditMode] = useState('playing');
  const [customHandicaps, setCustomHandicaps] = useState({});
  const [livePreviews, setLivePreviews] = useState(null);

  const totalPar = useMemo(
    () => course?.hole_pars?.reduce((acc, par) => acc + (par || 0), 0) || 0,
    [course]
  );
  const canEditAnyStats = (canEditCourseStats || canEditAllowance) && !isCurrentRoundFinalized;

  const teams = teamCompetition.teams || [];
  const teamRoundByTeamId = {};
  teamRounds.forEach((tr) => { teamRoundByTeamId[tr.competition_team_id] = tr; });
  const hasStarted = teamRounds.length > 0;
  // Stable team numbering (1, 2, 3…) — same order the Setup tab lists teams
  // in, so the same team gets the same number on the Scoring tab too.
  const teamNumberById = {};
  teams.forEach((team, i) => { teamNumberById[team.id] = i + 1; });

  const invalidateRoundData = () => queryClient.invalidateQueries(['roundData', tournamentId, roundNumber]);

  // The nominated scoring tee is a competition-level setting, so it is written
  // onto the competition's config rather than per team. Only meaningful before
  // the round starts: each TeamRound snapshots the nomination at creation, and
  // that snapshot is what its card is scored against thereafter.
  const handleScoringTeeChange = async (teeId) => {
    if (savingScoringTee) return;
    setSavingScoringTee(true);
    try {
      const config = { ...(teamCompetition.config || {}), round_number: roundNumber };
      if (teeId == null) delete config.scoring_tee_id;
      else config.scoring_tee_id = teeId;
      const res = await authenticatedFetch(`/competitions/${teamCompetition.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
      });
      if (!res.ok) {
        notify.error((await res.json().catch(() => ({}))).error || 'Could not set the scoring tee.');
        return;
      }
      invalidateRoundData();
    } finally {
      setSavingScoringTee(false);
    }
  };

  const handleStartTeamRounds = async () => {
    if (isStarting) return;
    setIsStarting(true);
    try {
      const res = await authenticatedFetch(`/competitions/${teamCompetition.id}/start-team-rounds`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom_handicaps: customHandicaps }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to start team rounds');
      // Starting is the hand-off from setting the round up to playing it, so
      // move to Scoring rather than leaving the organiser on the Setup tab
      // they just finished with — same as the individual page
      // (NewScorecardEntry.js's handleStartRound → setViewMode('junction')).
      // The panel shows Players for a beat until the refetch lands the new
      // team rounds, then follows; the tab bar's own isRoundStarted is already
      // team-aware, so it lights up at the same moment.
      setViewMode('junction');
      invalidateRoundData();
    } catch (e) {
      notify.error(e.message);
    } finally {
      setIsStarting(false);
    }
  };

  // Tapping a member's handicap: admins see the Playing/Index choice first
  // (matching NewScorecardEntry.js's openHcpEdit); anyone else goes straight
  // to the keypad in "playing" mode — the backend is the real gate either way
  // (member-handicap requires team membership, member-index requires
  // MANAGE_PLAYERS), this just matches the individual page's own affordance.
  const openHcpEdit = (member) => {
    if (canEditHandicapIndex) {
      setHcpChoiceMember(member);
    } else {
      setHcpEditMode('playing');
      setHcpKeypadMember(member);
    }
  };

  const handleHcpEditChoice = (mode) => {
    setHcpEditMode(mode);
    setHcpKeypadMember(hcpChoiceMember);
    setHcpChoiceMember(null);
  };

  const refreshLivePreview = async (overrides) => {
    try {
      const res = await authenticatedFetch(`/competitions/${teamCompetition.id}/team-handicap-preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ round_number: roundNumber, custom_handicaps: overrides }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const byId = {};
      (data.teams || []).forEach((t) => { byId[t.id] = t.preview; });
      setLivePreviews(byId);
    } catch {
      // Best-effort — the last-known preview (or the static one from
      // teamCompetition.teams[].preview) stays on screen either way.
    }
  };

  const handleHcpKeypadSave = async (value) => {
    const member = hcpKeypadMember;
    if (!member) return;
    const mode = hcpEditMode;
    setHcpKeypadMember(null);

    if (mode === 'index') {
      // A stored index change is permanent player data, not a one-off
      // override, so it's applied immediately whether or not the round has
      // started (mirrors handleSavePlayerIndex).
      try {
        const res = await authenticatedFetch(`/tournaments/${tournamentId}/team-rounds/member-index`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ round_number: roundNumber, player_id: member.player_id, handicap_index: value }),
        });
        if (!res.ok) throw new Error((await res.json()).error || 'Failed to update handicap index');
        invalidateRoundData();
      } catch (e) {
        notify.error(e.message);
      }
      return;
    }

    if (!hasStarted) {
      // Pre-start "Playing Handicap": a pending local override only —
      // nothing persists until Start Team Rounds — but refresh the live
      // preview immediately so the shown team handicap doesn't lag.
      const updated = { ...customHandicaps, [member.player_id]: value };
      setCustomHandicaps(updated);
      refreshLivePreview(updated);
      return;
    }

    try {
      const res = await authenticatedFetch(`/tournaments/${tournamentId}/team-rounds/member-handicap`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ round_number: roundNumber, player_id: member.player_id, playing_handicap: value }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update handicap');
      invalidateRoundData();
    } catch (e) {
      notify.error(e.message);
    }
  };

  const handleFinalize = async () => {
    if (isFinalizing) return;
    if (!window.confirm('Finalize this round? Scores can still be reopened for editing afterwards.')) return;
    setIsFinalizing(true);
    try {
      const res = await authenticatedFetch(`/tournaments/${tournamentId}/team-rounds/finalize`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ round_number: roundNumber }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to finalize round');
      invalidateRoundData();
    } catch (e) {
      notify.error(e.message);
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleReopen = async () => {
    if (!window.confirm('Reopen this round for editing?')) return;
    try {
      const res = await authenticatedFetch(`/tournaments/${tournamentId}/team-rounds/reopen_all`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ round_number: roundNumber }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to reopen round');
      invalidateRoundData();
    } catch (e) {
      notify.error(e.message);
    }
  };

  // Same three-step save as NewScorecardEntry.js's handleSaveCourseStats
  // (course/tee PUT, allowance PUT, then a recalculation pass) — only the
  // third step differs, pointed at the team-rounds recalculate route instead
  // of the individual one, since a team's derived handicap needs rebuilding
  // from every member's course handicap, not just one player's.
  const handleSaveTeamCourseStats = async ({ courseChanges, allowance, affectsHandicaps }) => {
    if (course && Object.keys(courseChanges).length > 0) {
      // The tee the organiser tapped, falling back to the round's own.
      const targetTeeId = editingTee?.id ?? course.tee_id;
      const body = targetTeeId
        ? { tees: [{ id: targetTeeId, ...courseChanges }] }
        : { ...courseChanges };
      const res = await authenticatedFetch(`/courses/${course.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update course');
    }

    if (allowance !== undefined) {
      const res = await authenticatedFetch(`/tournaments/${tournamentId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ matchplay_percentage: allowance }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update allowance');
    }

    if (hasStarted && !isCurrentRoundFinalized) {
      const res = await authenticatedFetch(`/tournaments/${tournamentId}/team-rounds/recalculate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ round_number: roundNumber, recompute_handicaps: !!affectsHandicaps }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Saved, but recalculation failed');
    }

    invalidateRoundData();
  };

  // A small numbered badge identifying which team a card/row belongs to —
  // deliberately a number, not initials or an icon, so it stays meaningful
  // even once teams are renamed away from the "Team 1" default.
  const TeamBadge = ({ number }) => (
    <div className="player-avatar-circle" style={{
      width: '38px', height: '38px', fontSize: '1rem', fontWeight: 700, flexShrink: 0,
    }}>
      {number}
    </div>
  );

  // The per-member handicap breakdown line — same shape whether it comes
  // from a pre-start preview or the stored post-start snapshot, so this is
  // shared by both states. Makes the calculation fully transparent (each
  // player's WHS index, course-adjusted handicap, and weight in the formula)
  // rather than just showing the final team handicap number, which is shown
  // separately, right next to this, in the prominent badge. Each member is
  // click-to-edit, same as an individual player's handicap on the standard
  // Rounds page.
  //
  // Each row is full-width and two-line (name on top, stats below) so the
  // edit icon always lands in the same spot regardless of name length or
  // handicap digit count — the previous inline-wrapping chip layout made
  // rows different widths and the pencil icon drift depending on content.
  const HandicapBreakdown = ({ members, onEditMember }) => {
    if (!members || members.length === 0) return null;
    return (
      <div className="team-member-list">
        {members.map((m) => (
          <button
            key={m.player_id}
            type="button"
            onClick={() => onEditMember(m)}
            className="team-member-row"
          >
            <span className="team-member-main">
              <span className="team-member-name">{m.name}</span>
              <span className="team-member-substats">
                WHS {m.handicap_index != null ? Number(m.handicap_index).toFixed(1) : '—'} &rarr; CH {m.course_handicap}
                {m.weight_pct != null && <span className="team-member-weight"> &middot; &times;{m.weight_pct}%</span>}
              </span>
            </span>
            <FiEditIcon size={16} className="team-member-edit-icon" />
          </button>
        ))}
      </div>
    );
  };

  // --- Setup tab: team roster, pre/post "Start Team Rounds" ---
  if (viewMode === 'players' || (!hasStarted && !isCurrentRoundFinalized)) {
    return (
      <div className="round-setup-card">
        <h3 className="setup-title">{hasStarted ? 'Course & Teams' : 'Ready to Tee Off?'}</h3>
        <p className="setup-subtitle">
          {hasStarted
            ? 'The course and the team handicaps for this round'
            : `${teamCompetition.name} — a shared-ball team format`}
        </p>

        <CourseStatsBar
          course={course}
          totalPar={totalPar}
          allowancePercent={allowancePercent}
          editable={canEditAnyStats}
          onEditClick={(tee, opts) => { setEditingTee(tee); setCourseStatsShowHoles(!!opts?.showHoles); setIsCourseStatsModalOpen(true); }}
          tees={courseTees}
          roundStarted={hasStarted}
        />

        {teams.length === 0 ? (
          <div style={{
            background: '#fff', border: '1px solid #f1f5f9', borderRadius: '12px',
            padding: '16px', textAlign: 'center', margin: '16px 0', color: '#5A6B7B',
          }}>
            No teams have been set up yet for {teamCompetition.name}.
            {canStart && (
              <div style={{ marginTop: '12px' }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => navigate(`/tournaments/${tournamentId}/competitions/manage`)}
                >
                  Set up teams
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {!hasStarted && courseTees?.length > 1 && (
              <div className="scoring-tee-nomination">
                <div className="stn-head">
                  <span className="stn-title">Scoring tee</span>
                  <span className="stn-sub">
                    One ball is in play, so all sides score off one card. Each
                    player's own tee still sets their Course Rating and Slope;
                    this tee sets the par used in every member's handicap.
                    The guidance advises the shortest tee in play.
                  </span>
                </div>
                <select
                  className="player-tee-picker"
                  aria-label="Scoring tee"
                  disabled={!canStart || savingScoringTee}
                  value={teamCompetition.scoring_tee_id == null ? '' : String(teamCompetition.scoring_tee_id)}
                  onChange={(e) => handleScoringTeeChange(
                    e.target.value === '' ? null : parseInt(e.target.value, 10))}
                >
                  <option value="">
                    {teamCompetition.scoring_tee_name
                      ? `Shortest in play (${teamCompetition.scoring_tee_name})`
                      : 'Shortest tee in play'}
                  </option>
                  {courseTees.map(t => (
                    <option key={t.id} value={String(t.id)}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}

            {!hasStarted && canStart && (
              <div className="setup-actions-top" style={{
                display: 'flex', justifyContent: 'center', marginBottom: '20px',
                paddingBottom: '16px', borderBottom: '1px solid #f1f5f9',
              }}>
                <button onClick={handleStartTeamRounds} className="btn-primary" disabled={isStarting}
                  style={{
                    padding: '0 24px', height: '42px', fontSize: '0.9rem', borderRadius: '12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    boxShadow: '0 4px 12px rgba(0, 119, 190, 0.25)', border: 'none', fontWeight: 600,
                  }}
                >
                  <FiCheck size={18} /> {isStarting ? 'Starting…' : 'Start Team Rounds'}
                </button>
              </div>
            )}

            <div className="player-setup-list">
              {teams.map((team) => {
                const tr = teamRoundByTeamId[team.id];
                // Post-start always reads the persisted snapshot. Pre-start
                // reads the live-recomputed preview (if an edit has been made
                // this session) falling back to the static one round-data
                // shipped with — never re-derives the weighting math itself.
                const preview = (!tr && livePreviews) ? livePreviews[team.id] : team.preview;
                const heading = tr ? tr.team_name : ((team.member_names || []).join(' & ') || team.name);
                const members = tr ? tr.member_handicaps_snapshot : preview?.members;
                const teamHandicap = tr ? tr.team_playing_handicap : preview?.team_handicap;
                return (
                  <div key={team.id} className="team-setup-card">
                    <div className="team-setup-header">
                      <div className="player-info-brief">
                        <TeamBadge number={teamNumberById[team.id]} />
                        <span className="p-name">{heading}</span>
                      </div>
                      <div className="hcp-edit-zone" style={{ gap: '12px' }}>
                        <div className="playing-hcp-display">
                          <div className="h-val" style={{ fontSize: '1.2rem' }}>{teamHandicap ?? '—'}</div>
                          <div className="h-label" style={{ fontSize: '0.65rem' }}>Team Hcp</div>
                        </div>
                      </div>
                    </div>
                    {members ? (
                      <HandicapBreakdown members={members} onEditMember={openHcpEdit} />
                    ) : (
                      <span className="p-whs" style={{ display: 'block', padding: '6px 4px 2px' }}>
                        Handicap preview unavailable — attach a course and player handicaps.
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {isCourseStatsModalOpen && (
          <CourseStatsEditModal
            isOpen={isCourseStatsModalOpen}
            onClose={() => { setIsCourseStatsModalOpen(false); setEditingTee(null); }}
            onSave={handleSaveTeamCourseStats}
            course={course}
            tee={editingTee}
            allowance={allowancePercent || 100}
            canEditCourse={canEditCourseStats && ((courseTees || []).length <= 1 || !!editingTee)}
            canEditAllowance={canEditAllowance && ((courseTees || []).length <= 1 || !editingTee)}
            roundStarted={hasStarted && !isCurrentRoundFinalized}
            initialShowHoles={courseStatsShowHoles}
          />
        )}

        <HandicapEditChoiceModal
          isOpen={!!hcpChoiceMember}
          playerName={hcpChoiceMember?.name}
          currentPlayingHandicap={hcpChoiceMember?.course_handicap}
          currentIndex={hcpChoiceMember?.handicap_index}
          onChoose={handleHcpEditChoice}
          onClose={() => setHcpChoiceMember(null)}
          playingNote="This round only — recalculates the whole team's handicap"
          indexNote="Player's index"
          footnote="Changing either recalculates the whole team's handicap and every entered score, not just this player's."
        />

        {hcpKeypadMember && (
          <HandicapKeypadModal
            isOpen={!!hcpKeypadMember}
            label={`${hcpKeypadMember.name} — ${hcpEditMode === 'index' ? 'Handicap Index' : 'Playing Handicap'}`}
            currentHandicap={hcpEditMode === 'index'
              ? (hcpKeypadMember.handicap_index ?? null)
              : (customHandicaps[hcpKeypadMember.player_id] ?? hcpKeypadMember.course_handicap ?? null)}
            noDecimal={hcpEditMode !== 'index'}
            onSave={handleHcpKeypadSave}
            onClose={() => setHcpKeypadMember(null)}
          />
        )}
      </div>
    );
  }

  // --- Scoring tab: mirrors the individual comp's junction — Enter Live Scores,
  // Review & Finalize, Go to Leaderboard (no per-team list; picking a team to
  // score happens on the team-aware Enter Scores page). ---
  if (viewMode === 'junction') {
    return (
      <div className="junction-container">
        <button
          className="junction-button primary"
          onClick={() => navigate(`/tournaments/${tournamentId}/scorecard-input`, { state: { courseSequence: roundNumber } })}
        >
          <div className="junction-button-icon"><FiEditIcon size={18} /></div>
          <div className="junction-button-content">
            <span className="junction-button-title">Enter Live Scores</span>
            <span className="junction-button-subtitle">Pick a team and score their round</span>
          </div>
          <div className="junction-button-arrow"><FiChevronRight size={18} /></div>
        </button>

        <button className="junction-button secondary" onClick={() => navigate(`/leaderboard?tournament=${tournamentId}&live=true`)}>
          <div className="junction-button-icon"><FiBarChart2 size={18} /></div>
          <div className="junction-button-content">
            <span className="junction-button-title">Go to Leaderboard</span>
            <span className="junction-button-subtitle">Live standings & results</span>
          </div>
          <div className="junction-button-arrow"><FiChevronRight size={18} /></div>
        </button>

        <button className="junction-button secondary" onClick={() => setViewMode('grid')}>
          <div className="junction-button-icon"><FiGrid size={18} /></div>
          <div className="junction-button-content">
            <span className="junction-button-title">
              {isCurrentRoundFinalized ? 'View Full Scorecard' : 'Review and Finalize Scorecard'}
            </span>
            <span className="junction-button-subtitle">
              {isCurrentRoundFinalized ? 'See all hole-by-hole results' : 'Team scores hole-by-hole, then end the round'}
            </span>
          </div>
          <div className="junction-button-arrow"><FiChevronRight size={18} /></div>
        </button>
      </div>
    );
  }

  // --- Finalise tab: team totals + hole-by-hole review + finalize/reopen ---
  // A shared ball is scored against the NOMINATED tee, not the round's own:
  // `TeamRound.tee_id` is set to it at start, and every nett and point the
  // server writes comes off its card. So the review sheet has to print that
  // tee's par and stroke index — read off the round's tee, a mixed-tee day
  // described a card nobody played, and coloured the scores against it.
  // Falls back to the round's card, which is what a single-tee day resolves to.
  // A full card or none of it, the same bar `holeDataForTee` sets on the
  // individual grid: half a tee's pars mixed with the round's is a card that
  // was never played either.
  const scoringTee = (courseTees || []).find(t => t.id === teamCompetition?.scoring_tee_id);
  const teeCard = scoringTee?.hole_pars?.length === 18 ? scoringTee : null;
  const holePars = (teeCard?.hole_pars) || course?.hole_pars || [];
  const holeSIs = (teeCard?.hole_stroke_indices?.length === 18
    ? teeCard.hole_stroke_indices : course?.hole_stroke_indices) || [];

  // One nine (front or back) of the team review grid. Teams are labelled by
  // number (1, 2, 3…) rather than name — a joined multi-player name never fits
  // the row label. Uses the per-hole gross/points the server already stored.
  const renderReviewNine = (startIdx, totalLabel) => {
    const holes = Array.from({ length: 9 }, (_, k) => startIdx + k); // 0-based indices
    const parTotal = holes.reduce((s, h) => s + (holePars[h] || 0), 0);
    return (
      <div className="trg-grid">
        <div className="trg-cell trg-head trg-rowlabel">Hole</div>
        {holes.map(h => <div key={`h-${h}`} className="trg-cell trg-head">{h + 1}</div>)}
        <div className="trg-cell trg-head trg-total">{totalLabel}</div>

        <div className="trg-cell trg-par trg-rowlabel">Par</div>
        {holes.map(h => <div key={`p-${h}`} className="trg-cell trg-par">{holePars[h] ?? '-'}</div>)}
        <div className="trg-cell trg-par trg-total">{parTotal || '-'}</div>

        <div className="trg-cell trg-si trg-rowlabel">SI</div>
        {holes.map(h => <div key={`s-${h}`} className="trg-cell trg-si">{holeSIs[h] ?? '-'}</div>)}
        <div className="trg-cell trg-si trg-total">-</div>

        {teamRounds.map((tr, ti) => {
          const num = teamNumberById[tr.competition_team_id];
          const byHole = {};
          (tr.hole_scores || []).forEach(hs => { byHole[hs.hole_number] = hs; });
          const rowClass = ti % 2 === 0 ? 'trg-row-even' : 'trg-row-odd';
          const grossTot = startIdx === 0 ? tr.gross_score_front_9 : tr.gross_score_back_9;
          const ptsTot = startIdx === 0 ? tr.stableford_front_9 : tr.stableford_back_9;
          return (
            <React.Fragment key={`t-${tr.id}-${startIdx}`}>
              <div className={`trg-cell trg-teamnum ${rowClass}`}>{num}</div>
              {holes.map(h => {
                const gross = byHole[h + 1]?.gross_score;
                return (
                  <div key={`g-${tr.id}-${h}`} className={`trg-cell trg-gross ${rowClass}`}>
                    {/* The shared marker — the same one the individual finalise
                        scorecard and the leaderboard's hole-by-hole card draw. */}
                    <ScoreMarker strokes={gross} par={holePars[h]} />
                  </div>
                );
              })}
              <div className={`trg-cell trg-gross trg-total ${rowClass}`}>{grossTot || '-'}</div>

              <div className={`trg-cell trg-ptslabel ${rowClass}`}>Pts</div>
              {holes.map(h => {
                const pts = byHole[h + 1]?.stableford_points;
                return <div key={`pt-${tr.id}-${h}`} className={`trg-cell trg-pts ${rowClass}`}>{pts ?? '-'}</div>;
              })}
              <div className={`trg-cell trg-pts trg-total ${rowClass}`}>{ptsTot ?? '-'}</div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className="round-setup-card">
      <h3 className="setup-title">Review & Finalize</h3>
      <p className="setup-subtitle">{isCurrentRoundFinalized ? 'This round is finalized' : 'Team scorecards for this round'}</p>

      {/* Compact legend — maps each grid number to its team name and total,
          replacing the old full totals table so the tab is just the scorecards. */}
      <div className="trg-legend">
        {teamRounds.map((tr) => (
          <div key={tr.id} className="trg-legend-row">
            <span className="trg-legend-badge">{teamNumberById[tr.competition_team_id]}</span>
            <span className="trg-legend-name">{tr.team_name}</span>
            <span className="trg-legend-stat">Hcp {tr.team_playing_handicap} · {tr.stableford_total ?? 0} pts</span>
          </div>
        ))}
      </div>

      {/* Hole-by-hole scorecard grid for every team (numbered per the legend
          above), styled like the individual scorecard so both comps read the
          same way. */}
      {course && holePars.length >= 18 && (
        <div className="trg-card">
          {renderReviewNine(0, 'Out')}
          {renderReviewNine(9, 'In')}
        </div>
      )}

      {canFinalize && (
        <div className="setup-actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          {isCurrentRoundFinalized ? (
            <button onClick={handleReopen} className="btn-primary"
              style={{ width: '100%', height: '48px', fontSize: '1rem', borderRadius: '12px', fontWeight: 600 }}>
              Reopen Round
            </button>
          ) : (
            <button onClick={handleFinalize} className="btn-primary" disabled={isFinalizing}
              style={{ width: '100%', height: '48px', fontSize: '1rem', borderRadius: '12px', fontWeight: 600 }}>
              {isFinalizing ? 'Finalising…' : 'Finalise Round'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamRoundPanel;
